import React, { createContext, useEffect, useState } from "react";
import { io } from "socket.io-client";
import useAppStore from "src/stores/appStore";
import { assistantIdSelector } from "src/stores/selectors/appSelectors";
import { getValidAccessToken } from "src/services/tokenRefresh";

const SocketContext = createContext(null);
const SERVER_DISCONNECT = "io server disconnect";
const MAX_AUTH_RECONNECTS = 5;

const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const assistantId = useAppStore(assistantIdSelector);

  // A socket is a connection to one agent, so it is keyed on the selected agent:
  // moving to a different agent opens a fresh socket, and everything else — token
  // refreshes, isAuthenticated flips, same-agent navigation — reuses the existing one.
  // There is deliberately no teardown on logout: logout redirects away and the socket
  // dies with the page. The auth callback re-reads the token on every (re)connect, so
  // the socket never needs rebuilding just because the token changed.
  useEffect(() => {
    if (!assistantId) {
      setSocket(null);
      return undefined;
    }

    const newSocket = io(import.meta.env.VITE_DASHBOARD_API_URL, {
      transports: ["websocket"],
      // Each agent gets its own connection. Without forceNew, socket.io multiplexes
      // every io() call onto one shared Manager, so tearing down the previous agent's
      // socket disturbs the new one — it can end up connected without ever firing
      // `connect`, so the notification room is never (re)joined.
      forceNew: true,
      auth: (cb) => {
        getValidAccessToken()
          .then((token) => cb({ token }))
          .catch(() => cb({ token: null }));
      }
    });

    setSocket(newSocket);

    return () => {
      newSocket.removeAllListeners();
      newSocket.disconnect();
      setSocket(null);
    };
  }, [assistantId]);

  useEffect(() => {
    if (!socket) return undefined;

    // Counts server-initiated disconnects since the last healthy connect.
    let authReconnects = 0;

    const handleConnect = () => {
      authReconnects = 0;
    };

    // The server ends the connection when it sees an expired/invalid token, and
    // socket.io does not auto-reconnect from a server-initiated disconnect. Reconnect
    // manually: the auth callback re-runs getValidAccessToken on each attempt, which
    // refreshes an expired token so the next handshake carries a valid one. Bounded so
    // a genuinely dead session can't hammer the server — the app's 401 handling takes
    // over from there. Transport drops (network blips) use other reasons and socket.io
    // reconnects those itself.
    const handleDisconnect = (reason) => {
      if (reason !== SERVER_DISCONNECT) return;
      if (authReconnects >= MAX_AUTH_RECONNECTS) {
        console.error("Socket giving up reconnect after repeated authentication failures");
        return;
      }
      authReconnects += 1;
      socket.connect();
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, [socket]);

  return (
    <SocketContext.Provider value={socket}>
      {children}
    </SocketContext.Provider>
  );
};

export { SocketProvider, SocketContext };
